import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import reduxStore from "../redux";
import { messages } from "../common/notification";
import { setDeviceSchedule } from "../redux/action/deviceSchedule";
import { reloadDeviceDetails } from "./api";
import { connectionOf, devicePending } from "./function";

// After a schedule is sent, how long to wait before each automatic re-check of
// that device. Spaced out so a device that takes a while costs few calls: five
// reads over about four minutes at most, and none once it has applied. After
// the last one the row keeps its Pending badge and its refresh button.
export const CHECK_DELAYS_MS = [10000, 20000, 40000, 60000, 120000];

// Re-checks devices with a just-sent schedule until they report it, so nobody
// has to press the row's refresh to find out. One timer per watched device;
// each check is the same single-device /details read the row refresh makes.
//
// - Applied: the device's overrides are dropped and its document replaced (as a
//   row refresh does), and a toast says so.
// - Still pending: checks again after the next delay, until they run out.
// - The browser tab is in the background: a due check waits until it is
//   visible again, so a forgotten tab makes no calls.
// - The property changes or the page closes: everything stops.
//
// Returns `watch(deviceId)` to start (or restart) watching a device, and
// `checkingIds` - deviceId -> true while its check is in flight - for the row's
// refresh button to spin.
export const usePendingAutoCheck = (propertyCode) => {
  const dispatch = useDispatch();
  // deviceId -> { attempt, timer, due }
  const watched = useRef(new Map());
  const [checkingIds, setCheckingIds] = useState({});

  const stopAll = useCallback(() => {
    for (const entry of watched.current.values()) clearTimeout(entry.timer);
    watched.current.clear();
    setCheckingIds({});
  }, []);

  // Stop when the property changes or the page closes.
  useEffect(() => stopAll, [propertyCode, stopAll]);

  const check = useCallback(
    async (deviceId) => {
      const entry = watched.current.get(deviceId);
      if (!entry) return;
      if (document.hidden) {
        entry.due = true;
        return;
      }
      entry.due = false;

      setCheckingIds((ids) => ({ ...ids, [deviceId]: true }));
      const fresh = await reloadDeviceDetails(deviceId);
      setCheckingIds(({ [deviceId]: done, ...rest }) => rest);

      // Stopped meanwhile (property changed, page closed, or watched afresh).
      if (watched.current.get(deviceId) !== entry) return;

      const state = reduxStore.getState().deviceSchedule;
      if (state?.property?.houseAccessCode !== propertyCode) return;

      const overrides = state.overrides?.[deviceId] || {};
      const stillPending = fresh ? devicePending(fresh, overrides) : true;

      if (fresh && !stillPending) {
        watched.current.delete(deviceId);
        const { [deviceId]: dropped, ...otherOverrides } = state.overrides || {};
        dispatch(
          setDeviceSchedule({
            details: { ...state.details, [deviceId]: fresh },
            overrides: otherOverrides,
          })
        );
        messages.success(`${deviceId}: the device has applied the schedule.`);
        return;
      }

      // Keep the fresher document either way; the overrides keep it pending.
      if (fresh) {
        dispatch(setDeviceSchedule({ details: { ...state.details, [deviceId]: fresh } }));
      }

      entry.attempt += 1;
      if (entry.attempt >= CHECK_DELAYS_MS.length) {
        watched.current.delete(deviceId);
        messages.notify(
          fresh && connectionOf(fresh).online === false
            ? `${deviceId} is offline. It will apply the schedule when it comes back online.`
            : `${deviceId} has not applied the schedule yet. Use its refresh button to check again later.`
        );
        return;
      }
      entry.timer = setTimeout(() => check(deviceId), CHECK_DELAYS_MS[entry.attempt]);
    },
    [dispatch, propertyCode]
  );

  // Checks that fell due while the tab was in the background run on return.
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      for (const [deviceId, entry] of watched.current) {
        if (entry.due) check(deviceId);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [check]);

  const watch = useCallback(
    (deviceId) => {
      const previous = watched.current.get(deviceId);
      if (previous) clearTimeout(previous.timer);
      const entry = { attempt: 0, due: false, timer: null };
      watched.current.set(deviceId, entry);
      entry.timer = setTimeout(() => check(deviceId), CHECK_DELAYS_MS[0]);
    },
    [check]
  );

  return { watch, checkingIds };
};
